import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  FindAllTransactionsByCommunityIdQueryVariables,
  FindTransactionsByCommunityIdResponse
} from "../../../../schemaTypes";
import { newTransactionSubscription } from "../FindTransactions";
import { WithFindTransactionsByCommunityId } from "../FindTransactionsByCommunityId";

// GraphQL query for transactions by communityId
export const findAllTransactionsByCommunityIdQuery = gql`
  query FindAllTransactionsByCommunityId(
    $communityId: String!
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
  ) {
    findAllTransactionsByCommunityId(
      communityId: $communityId
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
    ) {
      data {
        transaction {
          id
          amount
          charge
          reference
          type
          status
          currency
          transferTypes
          # wallet { 
          #   id 
          #   balance 
          #   owner { 
          #     id 
          #     customer { 
          #       firstname 
          #       lastname 
          #     } 
          #   } 
          #   createdAt 
          #   updatedAt 
          # }
          # recipientWallet { 
          #   id 
          #   balance 
          #   currency 
          #   owner { 
          #     admin { 
          #       merchantProfile { 
          #         companyName 
          #       } 
          #     } 
          #   } 
          #   createdAt 
          #   updatedAt 
          # }
          bankPreference { 
            id 
          }
          offer { 
            id 
            title 
            description 
            category { 
              name 
            } 
            createdBy{
              id
              role
              # admin{
              #   id
              #   merchantProfile{
              #     id
              #     companyName
              #     businessName
              #   }
              # }
            }
          }
          njoyCode
          partnerVoucher
          user { 
            id 
            customer{
              id
              firstname
              lastname
            } 
            # createdAt 
            # updatedAt 
          }
          createdAt
          updatedAt
        }
      }
      redeemedOffers
      totalSpend
      totalCustomers
      totalCommunitySize
      total
      page
      pageSize
      totalPages
    }
  }
`;

interface Props {
  variables: FindAllTransactionsByCommunityIdQueryVariables;
  children: (data: WithFindTransactionsByCommunityId) => React.ReactElement | null;
}

export const FindAllTransactionsByCommunityId: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    // { findTransactionsByCommunityId: TransactionPage },
    { findAllTransactionsByCommunityId: FindTransactionsByCommunityIdResponse },
    FindAllTransactionsByCommunityIdQueryVariables
  >(findAllTransactionsByCommunityIdQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  useSubscription(newTransactionSubscription);

  // Extract response fields to match updated resolver logic (all offers in community)
  const transactions = data?.findAllTransactionsByCommunityId?.data || [];
  const page = data?.findAllTransactionsByCommunityId?.page ?? variables.page;
  const pageSize = data?.findAllTransactionsByCommunityId?.pageSize ?? variables.pageSize;
  const total = data?.findAllTransactionsByCommunityId?.total ?? 0;
  const totalPages = data?.findAllTransactionsByCommunityId?.totalPages ?? 0;
  const hasMoreTransactions = page < totalPages;
  const redeemedOffers = data?.findAllTransactionsByCommunityId?.redeemedOffers ?? 0;
  const totalSpend = data?.findAllTransactionsByCommunityId?.totalSpend ?? 0;
  const totalCustomers = data?.findAllTransactionsByCommunityId?.totalCustomers ?? 0;
  const totalCommunitySize = data?.findAllTransactionsByCommunityId?.totalCommunitySize ?? 0;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllTransactionsByCommunityId) {
          return prev;
        }
        
        return {
          ...fetchMoreResult,
          findAllTransactionsByCommunityId: {
            ...fetchMoreResult.findAllTransactionsByCommunityId,
            data: [
              ...(prev?.findAllTransactionsByCommunityId?.data || []),
              ...(fetchMoreResult.findAllTransactionsByCommunityId.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any[],
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
    redeemedOffers,
    totalSpend,
    totalCustomers,
    totalCommunitySize
  });
};